import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useKpiBugs, useKpiReleases, useKpiDevelopPoints } from '../../../hooks/index.js';
import { extractDateString } from '../../../lib/date-utils.js';
import { escapeCsvValue, downloadCsv } from '../../../lib/csv.js';
import type { KpiTeam, BugDetail, ReleaseDetail, DevelopPointDetail } from '../../../types/api.js';
import { BugTable, dateText, discoveryDateOf } from './BugTable.js';
import { ReleaseTable } from './ReleaseTable.js';
import { DevelopPointTable } from './DevelopPointTable.js';

type ChartType = 'bugRate' | 'releasePoints' | 'developPoints' | 'releaseCount';

interface KpiDetailModalProps {
  open: boolean;
  onClose: () => void;
  team: KpiTeam;
  teamDisplayName: string;
  /** クリックされた日付 */
  date: string;
  /** 期間タイプ: '90d' = 過去90日間、'mtd' = 月初から当該日まで */
  periodType?: '90d' | 'mtd';
  /** クリックされたチャートの種類 */
  chartType?: ChartType;
}

type TabType = 'bugs' | 'releases' | 'developPoints';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チャート種別（bugRate/releasePoints/developPoints）に対応するデフォルト表示タブを決定
 * @graph-connects none
 */
function getDefaultTabForChartType(chartType: ChartType | undefined): TabType {
  switch (chartType) {
    case 'bugRate':
      return 'bugs';
    case 'releasePoints':
    case 'releaseCount':
      return 'releases';
    case 'developPoints':
      return 'developPoints';
    default:
      return 'bugs';
  }
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チャート種別に基づき表示すべきタブ種別の配列を決定（bugRate→bugs+releases、releasePoints→releases等）
 * @graph-connects none
 */
function getVisibleTabsForChartType(chartType: ChartType | undefined): TabType[] {
  switch (chartType) {
    case 'bugRate':
      // バグ発生率: バグとリリース両方表示（バグ発生率 = バグ数/リリースポイント）
      return ['bugs', 'releases'];
    case 'releasePoints':
    case 'releaseCount':
      return ['releases'];
    case 'developPoints':
      return ['developPoints'];
    default:
      return ['bugs', 'releases'];
  }
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPI詳細ドリルダウンモーダル。バグ・リリース・開発ポイントをタブ切り替えで一覧表示し、期間（90日/MTD）フィルタとCSVダウンロードを提供。バグCSVには発見日（初回発生日優先）・発見者・起因案件開発リーダーを含む
 * @graph-connects discoveryDateOf [calls] 発見日（初回発生日優先）算出
 * @graph-connects dateText [calls] CSV 出力時の日付正規化（BQ DATE オブジェクト対策）
 * @graph-connects useKpiBugs [calls] 指定チーム・期間のバグ詳細データを取得
 * @graph-connects useKpiReleases [calls] 指定チーム・期間のリリース詳細データを取得
 * @graph-connects useKpiDevelopPoints [calls] 指定チーム・期間の開発ポイント詳細データを取得
 * @graph-connects extractDateString [calls] 日付文字列正規化ユーティリティ呼び出し
 */
export function KpiDetailModal({
  open,
  onClose,
  team,
  teamDisplayName,
  date,
  periodType = '90d',
  chartType = 'bugRate',
}: KpiDetailModalProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>(() => getDefaultTabForChartType(chartType));
  const visibleTabs = getVisibleTabsForChartType(chartType);

  // BigQuery DATE型 { value: "YYYY-MM-DD" } やキャッシュされた古いデータに対応
  const displayDate = useMemo(() => extractDateString(date), [date]);

  // chartTypeが変わったらタブをリセット
  useEffect(() => {
    if (open) {
      setActiveTab(getDefaultTabForChartType(chartType));
    }
  }, [chartType, open]);

  // 期間タイプに基づいてデータを取得
  // '90d': 選択された日付を終点とする過去90日間
  // 'mtd': 選択された月の1日から当該日まで
  const params = useMemo(() => {
    if (!open || !displayDate) return null;

    const dateStr = displayDate;

    // YYYY-MM-DD形式の日付をパース（タイムゾーン問題を避けるため直接パース）
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;

    const endDate = new Date(year, month - 1, day);
    if (isNaN(endDate.getTime())) return null;

    const formatDate = (d: Date) => {
      const y = String(d.getFullYear());
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    let startDateObj: Date;
    if (periodType === 'mtd') {
      // 月初から当該日まで
      startDateObj = new Date(year, month - 1, 1);
    } else {
      // 過去90日間
      startDateObj = new Date(endDate);
      startDateObj.setDate(startDateObj.getDate() - 90);
    }

    return {
      team,
      startDate: formatDate(startDateObj),
      endDate: formatDate(endDate),
      limit: 500,
    };
  }, [open, displayDate, periodType, team]);

  // 開発ポイントは常にMTD（月初から当該日まで）
  const developPointParams = useMemo(() => {
    if (!open || !displayDate || chartType !== 'developPoints') return null;

    const dateStr = displayDate;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;

    const endDate = new Date(year, month - 1, day);
    if (isNaN(endDate.getTime())) return null;

    const formatDate = (d: Date) => {
      const y = String(d.getFullYear());
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    // MTD: 月初から当該日まで
    const startDateObj = new Date(year, month - 1, 1);

    return {
      team,
      startDate: formatDate(startDateObj),
      endDate: formatDate(endDate),
      limit: 500,
    };
  }, [open, displayDate, chartType, team]);

  // developPoints以外のチャートタイプの場合のみbugs/releasesをフェッチ
  const bugsReleasesParams = chartType === 'developPoints' ? null : params;

  const { data: bugsData, isLoading: isBugsLoading } = useKpiBugs(bugsReleasesParams);
  const { data: releasesData, isLoading: isReleasesLoading } = useKpiReleases(bugsReleasesParams);
  const { data: developPointsData, isLoading: isDevelopPointsLoading } =
    useKpiDevelopPoints(developPointParams);

  // バグCSVダウンロード
  const downloadBugsCsv = useCallback(() => {
    if (!bugsData?.data) return;
    const headers = [
      t('kpi.detail.createdDate'),
      t('kpi.detail.discoveryDate'),
      t('kpi.detail.causeProjectReleasedDate'),
      t('kpi.detail.issueKey'),
      t('kpi.detail.issueTitle'),
      t('kpi.detail.iPoint'),
      t('kpi.detail.foundBy'),
      t('kpi.detail.issueTypeName'),
      t('kpi.detail.classification'),
      t('kpi.detail.incidentLevel'),
      t('kpi.detail.status'),
      t('kpi.detail.cause'),
      t('kpi.detail.service'),
      t('kpi.detail.causeProductKey'),
      t('kpi.detail.causeProductSummary'),
      t('kpi.detail.causeProductDevLeader'),
      t('kpi.detail.devLeaderGroup'),
    ];
    const rows = bugsData.data.map((bug: BugDetail) => [
      escapeCsvValue(dateText(bug.createdDate)),
      escapeCsvValue(discoveryDateOf(bug)),
      escapeCsvValue(dateText(bug.causeProjectReleasedDate)),
      escapeCsvValue(bug.issueKey),
      escapeCsvValue(bug.summary),
      escapeCsvValue(bug.iPoint),
      escapeCsvValue(bug.foundBy),
      escapeCsvValue(bug.issueTypeName),
      escapeCsvValue(bug.classification),
      escapeCsvValue(bug.incidentLevel),
      escapeCsvValue(bug.status),
      escapeCsvValue(bug.cause),
      escapeCsvValue(bug.service),
      escapeCsvValue(bug.causeProjectKey),
      escapeCsvValue(bug.causeProjectSummary),
      escapeCsvValue(bug.causeProjectDevLeader),
      escapeCsvValue(
        bug.devLeaderGroupName != null || bug.devLeaderTeamName != null
          ? `${bug.devLeaderGroupName ?? ''} ${bug.devLeaderTeamName ?? ''}`.trim()
          : '',
      ),
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    downloadCsv(`bugs_${teamDisplayName}_${displayDate}.csv`, csv);
  }, [bugsData, teamDisplayName, t, displayDate]);

  // リリースCSVダウンロード
  const downloadReleasesCsv = useCallback(() => {
    if (!releasesData?.data) return;
    const headers = [
      t('kpi.detail.releasedDate'),
      t('kpi.detail.issueKey'),
      t('kpi.detail.summary'),
      t('kpi.detail.effectivePoint'),
      t('kpi.detail.foundBy'),
      t('kpi.detail.assignee'),
    ];
    const rows = releasesData.data.map((release: ReleaseDetail) => [
      escapeCsvValue(dateText(release.releasedDate)),
      escapeCsvValue(release.issueKey),
      escapeCsvValue(release.summary),
      escapeCsvValue(release.effectivePoint),
      escapeCsvValue(release.foundBy),
      escapeCsvValue(release.assignee),
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    downloadCsv(`releases_${teamDisplayName}_${displayDate}.csv`, csv);
  }, [releasesData, teamDisplayName, t, displayDate]);

  // 開発ポイントCSVダウンロード
  const downloadDevelopPointsCsv = useCallback(() => {
    if (!developPointsData?.data) return;
    const headers = [
      t('kpi.detail.completedDate'),
      t('kpi.detail.issueKey'),
      t('kpi.detail.summary'),
      t('kpi.detail.developPoint'),
      t('kpi.detail.assignee'),
    ];
    const rows = developPointsData.data.map((dp: DevelopPointDetail) => [
      escapeCsvValue(dp.completedDate),
      escapeCsvValue(dp.issueKey),
      escapeCsvValue(dp.summary),
      escapeCsvValue(dp.developPoint),
      escapeCsvValue(dp.assignee),
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    downloadCsv(`develop_points_${teamDisplayName}_${displayDate}.csv`, csv);
  }, [developPointsData, teamDisplayName, t, displayDate]);

  // chartTypeに基づいてモーダルのタイトルを決定
  const getModalTitle = (): string => {
    switch (chartType) {
      case 'bugRate':
        return t('kpi.detail.bugRateTitle');
      case 'releasePoints':
        return t('kpi.detail.releasePointsTitle');
      case 'developPoints':
        return t('kpi.detail.developPointsTitle');
      case 'releaseCount':
        return t('kpi.detail.releaseCountTitle');
      default:
        return t('kpi.detail.bugs');
    }
  };

  const isLoading =
    activeTab === 'bugs'
      ? isBugsLoading
      : activeTab === 'releases'
        ? isReleasesLoading
        : isDevelopPointsLoading;

  const handleDownload = () => {
    if (activeTab === 'bugs') {
      downloadBugsCsv();
    } else if (activeTab === 'releases') {
      downloadReleasesCsv();
    } else {
      downloadDevelopPointsCsv();
    }
  };

  const hasData =
    activeTab === 'bugs'
      ? (bugsData?.data.length ?? 0) > 0
      : activeTab === 'releases'
        ? (releasesData?.data.length ?? 0) > 0
        : (developPointsData?.data.length ?? 0) > 0;

  // 期間タイプに応じた説明文のi18nキーを選択
  const bugsDescKey =
    periodType === 'mtd' ? 'kpi.detail.bugsDescriptionMtd' : 'kpi.detail.bugsDescription';
  const releasesDescKey =
    periodType === 'mtd' ? 'kpi.detail.releasesDescriptionMtd' : 'kpi.detail.releasesDescription';
  // 開発ポイントは常にMTD
  const developPointsDescKey = 'kpi.detail.developPointsDescriptionMtd';

  const getDescription = () => {
    if (activeTab === 'bugs') {
      return t(bugsDescKey, { team: teamDisplayName, date: displayDate });
    } else if (activeTab === 'releases') {
      return t(releasesDescKey, { team: teamDisplayName, date: displayDate });
    } else {
      return t(developPointsDescKey, { team: teamDisplayName, date: displayDate });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      // 起因案件名（min-w-[1600px] のバグテーブル）まで読めるよう full より広げる
      className="md:max-w-[1700px]"
      title={getModalTitle()}
      description={getDescription()}
    >
      {/* Tabs + Download Button */}
      <div className="flex items-center justify-between mb-4 border-b border-[var(--color-border)]">
        <div className="flex gap-2">
          {visibleTabs.includes('bugs') && (
            <button
              onClick={() => {
                setActiveTab('bugs');
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'bugs'
                  ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {t('kpi.detail.bugs')} ({bugsData?.count ?? 0})
            </button>
          )}
          {visibleTabs.includes('releases') && (
            <button
              onClick={() => {
                setActiveTab('releases');
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'releases'
                  ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {t('kpi.detail.releases')} ({releasesData?.count ?? 0})
            </button>
          )}
          {visibleTabs.includes('developPoints') && (
            <button
              onClick={() => {
                setActiveTab('developPoints');
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'developPoints'
                  ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {t('kpi.detail.developPoints')} ({developPointsData?.count ?? 0})
            </button>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDownload}
          disabled={isLoading || !hasData}
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {t('kpi.detail.downloadCsv')}
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : activeTab === 'bugs' ? (
        <BugTable data={bugsData?.data ?? []} />
      ) : activeTab === 'releases' ? (
        <ReleaseTable data={releasesData?.data ?? []} />
      ) : (
        <DevelopPointTable data={developPointsData?.data ?? []} />
      )}
    </Modal>
  );
}
